/**
 * The workflow badges in the PO Line Detail modal header: who owes the next
 * move, and the line's status.
 *
 * Moved out of POLineDetailModal for Customer review 11 Sep (item 28) so
 * both read the same pendingProposal predicates as the Change Requests tab.
 */
import type { ReactNode } from 'react';
import type { PurchaseOrderLine } from '../../lib/api';
import { isPhantomChangeRequest } from './pendingProposal';
import { nextActionPartyFor } from './nextAction';
import { statusOf } from './lineStatus';
import { getOTIFStatusStyle, OTIF_BADGE_SHAPE, OTIF_BADGE_OUTLINE } from '../drilldown/drilldownUtils';

interface POLineHeaderBadgesProps {
  line: PurchaseOrderLine;
  /** How many times this line has been PUT on hold; omitted or 0 shows no count. */
  holdCount?: number | null;
  /** Rendered between the two badges — the header's existing order (CR-6510 concession badge). */
  children?: ReactNode;
}

export default function POLineHeaderBadges({ line, holdCount, children }: POLineHeaderBadgesProps) {
  const status = line.acknowledge_status;
  // CR-7416: this used to return EARLY here, rendering nothing at all. The OTIF
  // verdict has to show on this modal whatever the line's acknowledge status is
  // — an unacknowledged line still has a verdict, or still has none, and both
  // are answers the ticket asks for. So the status badge became conditional
  // instead of the whole header.
  const hasStatus = Boolean(status) || Boolean(line.on_hold);
  // CR-4882: a change_proposed_* status with no supplier_proposed_* value is
  // a stuck legacy row; treat it as resolved so no phantom owner is claimed.
  const isStuck = isPhantomChangeRequest(line);

  // CR-4704 round 4: "Waiting on Supplier" / "Waiting on Customer", matching
  // the PO Lines table. A `new` line is Supplier's because the supplier owes
  // the order acknowledgement (accept_delivery_date's initial accept) — not
  // because a change is pending. Customer review 11 Sep (item 28): an
  // imported supplier_proposed_date on such a line changes nothing here.
  //
  // Walkthrough issue 4 (Aaron, 17 Sep 2026): this used to do that mapping
  // INLINE from acknowledge_status, so it never learnt G7 and a line the
  // CUSTOMER had held still read "Waiting on Supplier" here while the grid
  // read "Waiting Customer" — on a line the supplier cannot touch at all.
  // nextActionPartyFor is now the one answer. The phantom guard stays on top
  // of it, and does NOT apply to a held line: the hold names its own owner
  // whatever the change request looks like.
  const owner = isStuck && !line.on_hold ? null : nextActionPartyFor(line);

  // Walkthrough issues 5 and 7 (Aaron, 17 Sep 2026). Issue 7: *"there's nothing
  // in the PO line modal to tell me what status it is"* — the badge below used
  // to render only when `status !== 'new'`, so the commonest state of all showed
  // nothing and this was the one surface with no status on it. Issue 5: the
  // modal carried a SEPARATE red "On Hold" chip beside a status badge saying
  // something else, which is the contradiction he asked us to end. One badge
  // now, from `statusOf`, which answers the hold first.
  const { label, className, title } = statusOf(line);

  // CR-7416: the verdict the OTIF drill-down would print for this same line.
  const otif = getOTIFStatusStyle(line.is_otif, line.status);

  // Walkthrough batch 2 (Aaron, 17 Sep 2026), two changes, both about reading
  // order:
  //
  // 1. STATUS COMES FIRST, then who owes the next move. His order, stated on
  //    the walk. The modal header puts % Delivered after both.
  // 2. THE HOLD COUNT SAYS WHAT IT COUNTS. It used to be a bare number tucked
  //    inside the status badge, so a `new` line read "New 6" and the 6 belonged
  //    to nothing on screen: *"I'm not sure what that 6 corresponds to."* It is
  //    now its own chip that says "6 holds" — the same figure (times PUT on
  //    hold, never releases: walkthrough issue 8), just legible without a
  //    hover. It sits beside the status because that is what it is about.
  return (
    <>
      {hasStatus && (
        <span
          className={`px-3 py-1 rounded-full font-semibold text-xs ${className}`}
          title={title}
        >
          {label}
        </span>
      )}
      {!!holdCount && holdCount > 0 && (
        <span
          className="px-2 py-1 rounded-full font-medium text-xs bg-orange-50 text-orange-700 border border-orange-200"
          title={`This line has been put on hold ${holdCount} time${holdCount === 1 ? '' : 's'}`}
        >
          {holdCount} hold{holdCount === 1 ? '' : 's'}
        </span>
      )}
      {/* CR-7418 round 3: slate, not blue. This badge says whose turn it is —
          information, not a state — and its pale blue was identical to the OTIF
          "Acknowledged" chip that can sit beside it, with the solid blue "New"
          chip on its other side. Three meanings, one hue. */}
      {owner && (
        <span className="px-3 py-1 rounded-full font-semibold text-xs bg-slate-100 text-slate-700 border border-slate-300">
          Waiting on {owner}
        </span>
      )}
      {/* CR-7416 (Aaron, 19 Sep): *"current OTIF status should show on the po
          line modal to match the OTIF click through, either Pending, Pass,
          Fail"*. From `getOTIFStatusStyle`, the same function the drill-downs
          ask, so the two surfaces agree BY CONSTRUCTION rather than by two
          implementations kept in step — including the part that is easy to get
          wrong on a second pass: an OPEN line reads Pending however its stored
          `is_otif` reads, because a line reaches no verdict until it closes
          (CR-7270).

          Placed after the next-action badge and before % Delivered, which is
          Aaron's reading order from the 17 Sep walk: what the line IS, then who
          owes the next move, then how it has performed. The verdict is a
          judgment about the past and does not belong ahead of either.

          It is labelled, unlike the drill-down cell. In the drill-down the
          column heading says OTIF; here the badge stands alone in a row of
          badges, and a bare "Pending" beside "New" would be one more word with
          nothing saying what it is about.

          And it is OUTLINED here where the drill-down fills it. The chip to its
          right is the pale-green "% Delivered" pill, and two near-identical
          green chips side by side is what Aaron rejected on this same header on
          19 Sep (CR-7418 round 3b: the words were distinguishable and the eye
          still read one blob). Outlined makes it a different class of chip
          without giving up green-means-pass, and it follows that round's own
          "outlined, never filled" precedent rather than inventing a treatment.
          The header's full palette inventory is in CR-7418's record. */}
      <span
        className={`${OTIF_BADGE_SHAPE} ${OTIF_BADGE_OUTLINE[otif.tone]}`}
        title={otif.judged
          ? `OTIF verdict: ${otif.label}`
          : 'OTIF is judged when the line closes — this line has not reached a verdict yet'}
      >
        OTIF {otif.label}
      </span>
      {children}
    </>
  );
}
